import { useEffect, useState } from "react";
import { Check, X, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const STATUS_LABELS = { pending: "En attente", approved: "Publiée", rejected: "Rejetée" };
const STATUS_STYLES = {
  pending: "border-orange-500/40 bg-orange-500/10 text-orange-400",
  approved: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  rejected: "border-red-500/40 bg-red-500/10 text-red-400",
};

export default function FanWallTab() {
  const [photos, setPhotos] = useState([]);

  const load = () => api.get("/admin/fan-photos").then((r) => setPhotos(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const moderate = async (id, status) => {
    try {
      await api.patch(`/admin/fan-photos/${id}`, { status });
      toast.success(status === "approved" ? "Photo publiée sur le mur" : "Photo rejetée");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette photo ?")) return;
    try {
      await api.delete(`/admin/fan-photos/${id}`);
      toast.success("Photo supprimée");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (photos.length === 0) {
    return (
      <p data-testid="fan-photos-empty" className="text-slate-500">
        Aucune photo de fan pour le moment. Les photos envoyées depuis la page d'accueil apparaîtront ici pour validation.
      </p>
    );
  }

  const pending = photos.filter((p) => p.status === "pending");
  const others = photos.filter((p) => p.status !== "pending");

  const PhotoCard = ({ p }) => (
    <div className="card-tactical overflow-hidden" data-testid={`admin-fan-photo-${p.id}`}>
      <img src={p.image_url} alt={`Photo de ${p.pseudo}`} className="aspect-square w-full object-cover" />
      <div className="p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate font-heading text-sm font-bold uppercase">{p.pseudo}</p>
          <span className={`rounded border px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLES[p.status]}`}>
            {STATUS_LABELS[p.status]}
          </span>
        </div>
        <p className="mt-1 text-[11px] text-slate-500">{new Date(p.created_at).toLocaleString("fr-FR")}</p>
        <div className="mt-3 flex gap-2">
          {p.status !== "approved" && (
            <button onClick={() => moderate(p.id, "approved")} data-testid={`fan-photo-approve-${p.id}`}
              className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-emerald-500/40 px-2 py-1.5 text-xs font-bold uppercase text-emerald-400 transition-all hover:bg-emerald-500/10">
              <Check className="h-3.5 w-3.5" /> Publier
            </button>
          )}
          {p.status !== "rejected" && (
            <button onClick={() => moderate(p.id, "rejected")} data-testid={`fan-photo-reject-${p.id}`}
              className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-orange-500/40 px-2 py-1.5 text-xs font-bold uppercase text-orange-400 transition-all hover:bg-orange-500/10">
              <X className="h-3.5 w-3.5" /> Rejeter
            </button>
          )}
          <button onClick={() => remove(p.id)} data-testid={`fan-photo-delete-${p.id}`} aria-label="Supprimer la photo"
            className="rounded-lg border border-red-500/40 p-1.5 text-red-400 transition-all hover:bg-red-500/10">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="grid gap-8" data-testid="fan-wall-admin">
      <div>
        <h3 className="mb-3 font-heading text-lg font-bold uppercase text-orange-400">
          À valider ({pending.length})
        </h3>
        {pending.length === 0 ? (
          <p className="text-sm text-slate-500">Aucune photo en attente de validation.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{pending.map((p) => <PhotoCard key={p.id} p={p} />)}</div>
        )}
      </div>
      <div>
        <h3 className="mb-3 font-heading text-lg font-bold uppercase text-slate-300">
          Historique ({others.length})
        </h3>
        {others.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{others.map((p) => <PhotoCard key={p.id} p={p} />)}</div>
        )}
      </div>
    </div>
  );
}
